import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { CNPJ_PATTERN, isValidCnpj } from '@/shared/format/cnpj';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { CREDIT_NOTE_LIST_PATH } from './credit-note-list.screen';
import { creditNoteClone, creditNoteCloneAsNew, creditNoteRecoverSimulation, creditNoteSave } from './credit-note-new.screen';
import { CREDIT_NOTE_STATUS } from './credit-note.status';
import type { CreditNote } from './credit-note.wire';

// Manifesto do detalhe (U23/U42), leve: as ações com efeito declaram entrada e `preview` (U24), sem alvo, e
// só rodam pela tela, que as prende à operação aberta. Ficam de fora do agente: assinatura
// (aprovar/rejeitar), consulta paga de bureau (X01) e o que depende de formulário do legado.

/** Abas do legado, na mesma ordem (U44; discovery/02 §3.3). As condicionais saem por `hidden` na tela. */
export const CREDIT_NOTE_TABS = [
  { id: 'informacoes', label: msg`Informações` },
  { id: 'dados-bancarios', label: msg`Dados bancários` },
  { id: 'garantias', label: msg`Garantias` },
  { id: 'envolvidos', label: msg`Envolvidos` },
  { id: 'operacao', label: msg`Operação` },
  { id: 'documentos', label: msg`Documentos` },
  { id: 'assinaturas', label: msg`Assinaturas` },
  { id: 'kyc', label: msg`KYC` },
  { id: 'credito', label: msg`Crédito` },
  { id: 'cessoes', label: msg`Cessões` },
  { id: 'historico', label: msg`Histórico` },
] as const;
export type CreditNoteTab = (typeof CREDIT_NOTE_TABS)[number]['id'];
export const CREDIT_NOTE_TAB_IDS = CREDIT_NOTE_TABS.map((tab) => tab.id) as [CreditNoteTab, ...CreditNoteTab[]];

/**
 * `?tab=` só aceita as abas do manifesto; a primeira não vai para a URL (U42). `?sheet=` abre o painel de filtros da aba
 * Cessões no iPhone (U26). Leve, para a rota.
 */
export const creditNoteDetailSearch = z.object({ tab: z.enum(CREDIT_NOTE_TAB_IDS).optional().catch(undefined), sheet: z.string().max(40).optional().catch(undefined) });

/* eslint-disable lingui/no-unlocalized-strings -- respostas e descrições ao modelo, inglês canônico (U33) */
export const OPEN_CREDIT_NOTE_ONLY = 'This action only runs on the credit operation detail screen, on the operation that is open there.';
/* eslint-enable lingui/no-unlocalized-strings */
/**
 * Toda ferramenta com efeito do detalhe fica presa à operação aberta (B1 da !11138, o padrão de
 * `wallet-agent.ts`): a entrada não tem `id`, `no` nem alvo livre, e fora do detalhe não há operação. A tela
 * troca `run` e `preview` pelos do registro carregado, só quando a ação está visível na barra e pelo mesmo
 * caminho dela (`screens/credit-note-agent.ts`).
 */
const needsOpenNote = () => {
  throw new Error(OPEN_CREDIT_NOTE_ONLY);
};

export const creditNoteRefresh = action({
  id: 'creditNote.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados da operação aberta. Não muda nada.`,
  run: () => undefined,
});

export const creditNoteSubmitApprovalInput = z.strictObject({
  // eslint-disable-next-line lingui/no-unlocalized-strings -- descrição ao modelo, inglês canônico (U33)
  pastStartDate: z.enum(['keep', 'update']).optional().describe('Only when the start date of the open operation is in the past (the screen asks): keep sends the current proposal, update saves today as the start date and sends it 2 s later'),
});
export const creditNoteSubmitApproval = action<z.infer<typeof creditNoteSubmitApprovalInput>>({
  id: 'creditNote.submitApproval',
  kind: 'write',
  label: msg`Enviar para aprovação`,
  description: msg`Envia a operação aberta na tela, em Rascunho, Revisão, Reprovado ou Erro, para a primeira etapa de aprovação. Com a data de início no passado, pastStartDate é obrigatório, como no diálogo da tela: keep mantém a proposta atual, update grava hoje como data de início e envia em seguida. Fora disso, vale a configuração do correspondente, como na tela. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'SubmitApproval'),
  input: creditNoteSubmitApprovalInput,
  preview: () => ({ title: i18n._(msg`Enviar a operação para aprovação?`), detail: i18n._(msg`A operação sai de Rascunho e vai para a primeira etapa de aprovação da esteira.`) }),
  run: needsOpenNote,
});

export const creditNoteApproveInput = z.strictObject({ message: z.string().max(500).optional() });
function approve(id: string, text: MessageDescriptor, description: MessageDescriptor, permission: string) {
  return action<z.infer<typeof creditNoteApproveInput>>({
    id,
    kind: 'write',
    label: text,
    description,
    permission: can('CreditNote', permission),
    input: creditNoteApproveInput,
    preview: () => ({ title: i18n._(msg`Aprovar a operação?`), detail: i18n._(msg`A operação segue para a próxima etapa da esteira.`) }),
    run: needsOpenNote,
  });
}

/* eslint-disable lingui/no-unlocalized-strings -- nome da permissão do fio (U33), não texto de UI */
export const creditNoteApproveCompliance = approve(
  'creditNote.approveCompliance',
  msg`Aprovar compliance`,
  msg`Aprova a etapa de compliance da operação aberta na tela, com mensagem opcional. Só enquanto ela está em Aprovação de Compliance. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  'ComplianceApproval',
);
export const creditNoteApproveCredit = approve(
  'creditNote.approveCredit',
  msg`Aprovar crédito`,
  msg`Aprova a etapa de crédito da operação aberta na tela, com mensagem opcional. Só enquanto ela está em Aprovação de Crédito. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  'CreditApproval',
);
export const creditNoteApproveInstrument = approve(
  'creditNote.approveInstrument',
  msg`Aprovar instrumento`,
  msg`Aprova a etapa de instrumento da operação aberta na tela, com mensagem opcional. Só enquanto ela está em Aprovação de Instrumento. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  'InstrumentApproval',
);
/* eslint-enable lingui/no-unlocalized-strings */

const rejectFields = z.strictObject({
  // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio ou descrição do agente em inglês (U33), não texto de UI
  reasons: z.array(z.string().min(1)).min(1).describe('Rejection reason codes offered by the screen form (GET /RejectionReason); any other code is refused'),
  block: z.enum(['none', 'Person', 'Operation']).default('none'),
  // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio ou descrição do agente em inglês (U33), não texto de UI
  blockUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().describe('Civil date YYYY-MM-DD, required when block is not none; sent as the ISO instant of that midnight (-03:00), like the screen'),
  // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio ou descrição do agente em inglês (U33), não texto de UI
  blacklist: z.array(z.string()).optional().describe('Borrower fields to blacklist offered by the screen form (registrationnumber, email); reject sends them only with block Person, cancel with any block'),
  message: z.string().max(500).optional(),
});
/** Com bloqueio, a validade é obrigatória: a mesma regra do `disapprovalSchema` da tela. */
const blockUntilRule = (input: { block: string; blockUntil?: string | undefined }, context: z.RefinementCtx) => {
  if (input.block !== 'none' && !input.blockUntil) context.addIssue({ code: 'custom', path: ['blockUntil'], message: 'blockUntil is required when block is not none' });
};
export const rejectInput = rejectFields.superRefine(blockUntilRule);
export const cancelInput = rejectFields.extend({ message: z.string().min(1).max(500) }).superRefine(blockUntilRule);
export type RejectInput = z.output<typeof rejectInput>;

function reject(id: string, text: MessageDescriptor, permission: string) {
  return action<RejectInput>({
    id,
    kind: 'destructive',
    label: text,
    description: msg`Rejeita a operação aberta na tela com os códigos de motivo, bloqueio opcional (none, Person ou Operation) até uma data e campos de blacklist, como o formulário da tela: a mensagem livre só vai com o motivo OUTROS_MOTIVOS. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
    permission: can('CreditNote', permission),
    input: rejectInput,
    preview: (input) => {
      const reasons = input.reasons.join(', ');
      return { title: i18n._(msg`Rejeitar a operação?`), detail: i18n._(msg`Motivos: ${reasons}. A operação volta para Reprovado.`), tone: 'destructive' };
    },
    run: needsOpenNote,
  });
}

// eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio ou descrição do agente em inglês (U33), não texto de UI
export const creditNoteRejectCompliance = reject('creditNote.rejectCompliance', msg`Rejeitar compliance`, 'ComplianceApproval');
// eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio ou descrição do agente em inglês (U33), não texto de UI
export const creditNoteRejectCredit = reject('creditNote.rejectCredit', msg`Rejeitar crédito`, 'CreditApproval');

export const creditNoteCancel = action<RejectInput>({
  id: 'creditNote.cancel',
  kind: 'destructive',
  label: msg`Cancelar operação`,
  description: msg`Cancela a operação aberta na tela com códigos de motivo, mensagem obrigatória e bloqueio opcional. Não vale em Garantia, salvo produto Zetra na UY3. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'Cancel'),
  input: cancelInput,
  preview: () => ({ title: i18n._(msg`Cancelar a operação?`), detail: i18n._(msg`A operação vai para Cancelada e sai da esteira.`), tone: 'destructive' }),
  run: needsOpenNote,
});

export const creditNoteNoInput = z.strictObject({});
export const creditNoteDelete = action({
  id: 'creditNote.delete',
  kind: 'destructive',
  label: msg`Excluir operação`,
  description: msg`Exclui a operação aberta na tela (ela vai para Excluídos, onde pode ser restaurada) e volta para a lista. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'Delete'),
  input: creditNoteNoInput,
  preview: () => ({ title: i18n._(msg`Excluir a operação?`), detail: i18n._(msg`A operação sai da lista e vai para Excluídos.`), tone: 'destructive' }),
  run: needsOpenNote,
});

export const creditNoteUnlock = action({
  id: 'creditNote.unlock',
  kind: 'write',
  label: msg`Desbloquear operação`,
  description: msg`Remove o bloqueio da operação aberta na tela, em Rascunho, Aprovação de Compliance, Revisão ou Liquidação Manual. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'ComplianceApproval'),
  input: creditNoteNoInput,
  preview: () => ({ title: i18n._(msg`Desbloquear a operação?`), detail: i18n._(msg`O bloqueio da operação é removido.`) }),
  run: needsOpenNote,
});

// ─── ações da barra (trilha "barra"): só as de corpo curto e autossuficiente ────
// Ficam só na tela (sem ferramenta): atribuir e compartilhar (dão acesso ao registro), definir status
// (ajuste administrativo), tokens e reservas (escolha de token), verificar proposta e calendário
// (consultas externas) e o download do carnê.

export const creditNoteCommentInput = z.strictObject({ comment: z.string().min(3).max(500) });
export const creditNoteComment = action<z.infer<typeof creditNoteCommentInput>>({
  id: 'creditNote.comment',
  kind: 'write',
  label: msg`Deixar comentário`,
  description: msg`Acrescenta um comentário, de 3 a 500 caracteres, à operação aberta na tela. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  input: creditNoteCommentInput,
  preview: (input) => ({ title: i18n._(msg`Comentar na operação?`), detail: input.comment }),
  run: needsOpenNote,
});

export const creditNoteAccountOpeningReviewInput = z.strictObject({ message: z.string().min(1).max(500) });
export const creditNoteAccountOpeningReview = action<z.infer<typeof creditNoteAccountOpeningReviewInput>>({
  id: 'creditNote.accountOpeningReview',
  kind: 'write',
  label: msg`Concluir revisão de abertura de conta`,
  description: msg`Conclui a revisão de abertura de conta da operação aberta na tela, em Revisão de abertura de conta (roteiro de liquidação com conta transitória), com uma justificativa obrigatória. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'LiquidationApproval'),
  input: creditNoteAccountOpeningReviewInput,
  preview: (input) => ({ title: i18n._(msg`Concluir a revisão de abertura de conta?`), detail: input.message }),
  run: needsOpenNote,
});

export const creditNoteLinkEmployerInput = z.strictObject({
  employerName: z.string().min(1),
  // eslint-disable-next-line lingui/no-unlocalized-strings -- mensagem ao modelo, inglês canônico (U33)
  employerRegistrationNumber: z.string().regex(CNPJ_PATTERN).refine(isValidCnpj, 'Invalid CNPJ check digits'),
  includeWebCred: z.boolean().optional(),
});
export const creditNoteLinkEmployer = action<z.infer<typeof creditNoteLinkEmployerInput>>({
  id: 'creditNote.linkEmployer',
  kind: 'write',
  label: msg`Associar empregador`,
  description: msg`Associa um empregador (nome e CNPJ válido, 14 caracteres sem máscara) à operação de consignado privado ou público aberta na tela e, se pedido, envia ao WebCred. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'ManualUpdate'),
  input: creditNoteLinkEmployerInput,
  preview: (input) => {
    const employer = input.employerName;
    return { title: i18n._(msg`Associar o empregador à operação?`), detail: i18n._(msg`Empregador: ${employer}.`) };
  },
  run: needsOpenNote,
});

export const creditNoteSetClosureStatusInput = z.strictObject({ liquidationClosureStatus: z.union([z.literal(0), z.literal(5), z.literal(6)]), reason: z.string().trim().min(10) });
export const creditNoteSetClosureStatus = action<z.infer<typeof creditNoteSetClosureStatusInput>>({
  id: 'creditNote.manualUpdateClosureStatus',
  kind: 'write',
  label: msg`Definir status roteiro`,
  description: msg`Define o status do roteiro de liquidação da operação aberta na tela, Encerrada e em roteiro: 0 Pendente, 5 Cancelado ou 6 Finalizado, com um motivo de pelo menos 10 caracteres. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'ManualUpdate'),
  input: creditNoteSetClosureStatusInput,
  preview: (input) => ({ title: i18n._(msg`Alterar o status do roteiro da operação?`), detail: input.reason }),
  run: needsOpenNote,
});

export const creditNoteRemoveRestrictionsInput = z.strictObject({ ids: z.array(z.string().min(1)).min(1) });
export const creditNoteRemoveRestrictions = action<z.infer<typeof creditNoteRemoveRestrictionsInput>>({
  id: 'creditNote.removeRestrictions',
  kind: 'destructive',
  label: msg`Remover restrições`,
  description: msg`Remove restrições de compliance do tomador da operação aberta na tela. ids tem de estar entre as restrições do tomador listadas no contexto da tela (campo restrictions); qualquer outro id é recusado. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  input: creditNoteRemoveRestrictionsInput,
  preview: (input) => {
    const count = input.ids.length;
    return { title: i18n._(msg`Remover ${count} restrição(ões) de compliance?`), detail: i18n._(msg`O tomador deixa de ter essas restrições.`), tone: 'destructive' };
  },
  run: needsOpenNote,
});

// ─── trilha "liquidação": só as de corpo autossuficiente ─────────────────────
// Ficam só na tela: aprovar o roteiro (uma decisão por pagamento), revisar a conta transitória (dados
// bancários) e "Concluir revisão de pagamento" (reenvia pagamentos com dinheiro e contas; unidade 10D).

const civil = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const creditNoteCloseTransitoryAccountInput = z.strictObject({
  // eslint-disable-next-line lingui/no-unlocalized-strings -- descrição ao modelo, inglês canônico (U33)
  cosifAccountCode: z.string().min(1).describe('A credit COSIF account with an external account id, among the ones the screen form offers (GET /CosifAccount?transactionType=Transfer)'),
  paymentDate: civil,
  closeTransitoryBankAccount: z.boolean().optional(),
});
export const creditNoteCloseTransitoryAccount = action<z.infer<typeof creditNoteCloseTransitoryAccountInput>>({
  id: 'creditNote.closeTransitoryAccount',
  kind: 'write',
  label: msg`Encerrar conta transitória`,
  description: msg`Encerra a conta transitória do roteiro da operação aberta na tela (Encerrada, em roteiro de liquidação, com status do roteiro Pendente ou Cancelado): transfere o saldo para uma conta COSIF de crédito com conta externa na data de pagamento (AAAA-MM-DD) e, se pedido, encerra a conta. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'LiquidationApproval'),
  input: creditNoteCloseTransitoryAccountInput,
  preview: (input) => {
    const account = input.cosifAccountCode;
    const date = input.paymentDate.split('-').reverse().join('/');
    const closing = input.closeTransitoryBankAccount === true ? i18n._(msg`Sim`) : i18n._(msg`Não`);
    return { title: i18n._(msg`Encerrar a conta transitória da operação?`), detail: i18n._(msg`Conta de crédito: ${account}. Data de pagamento: ${date}. Encerrar conta transitória: ${closing}.`) };
  },
  run: needsOpenNote,
});

// Unidade 6B: "Enviar para carteira" (InstallmentCreditNoteContainer), sem entrada: o convênio e as réguas são do produto.
export const creditNoteSendToWallet = action({
  id: 'creditNote.sendToWallet',
  kind: 'write',
  label: msg`Enviar para carteira`,
  description: msg`Envia a operação aberta na tela para a Gestão de Carteira, com o convênio e as réguas de cobrança e de comunicação do produto. Só em Encerrado ou Liquidação Manual, sem cobrança gerada; sem convênio no produto, a tela trava o envio. Não recebe id: a operação é sempre a aberta na tela e não pode ser escolhida; para agir em outra, abra-a antes. Pede a confirmação da pessoa.`,
  permission: can('BillingAccount', 'SendToWalletManagement'),
  input: creditNoteNoInput,
  preview: () => ({ title: i18n._(msg`Enviar a operação para a carteira?`), detail: i18n._(msg`A operação vai para a Gestão de Carteira com o convênio e as réguas do produto.`) }),
  run: needsOpenNote,
});

// ─── aba Documentos (trilha "abas") ─────────────────────────────────────────
// As chamadas próprias da aba, presas à operação da rota: a tela troca `preview` e `run` pelos do registro
// carregado (`useTabsAgentScreen`). Sem a operação aberta, a ferramenta recusa. Tudo da assinatura
// (solicitar, iniciar, cancelar, reenviar, signatários) e enviar arquivo ficam só na tela (U24, X01).

// eslint-disable-next-line lingui/no-unlocalized-strings -- descrição do agente em inglês (U33), não texto de UI
const documentRef = z.strictObject({ uploadId: z.string().min(1).describe('Document id (uploads[].id of the open credit operation)') });
/* eslint-disable lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33) */
const NOT_OPEN = 'Open the credit operation detail first: this action works only on the operation of the current screen.';
/* eslint-enable lingui/no-unlocalized-strings */
const refuse = () => {
  throw new Error(NOT_OPEN);
};

export const creditNoteDeleteDocument = action<z.infer<typeof documentRef>>({
  id: 'creditNote.deleteDocument',
  kind: 'destructive',
  label: msg`Excluir documento`,
  description: msg`Exclui um documento já gravado da operação aberta na tela (uploadId, o id do documento). Não vale em Revisão de Garantia. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'Update'),
  input: documentRef,
  preview: (input) => ({ title: i18n._(msg`Excluir o documento ${input.uploadId}?`), tone: 'destructive' }),
  run: refuse,
});

/** As ações da aba Documentos que a tela prende ao registro. */
export const CREDIT_NOTE_DOCUMENT_ACTIONS = [creditNoteDeleteDocument] as const;

const tabIds = CREDIT_NOTE_TAB_IDS.join(', ');

export const creditNoteOpenTab = action<{ tab: CreditNoteTab }>({
  id: 'creditNote.openTab',
  kind: 'navigate',
  label: msg`Abrir aba`,
  description: msg`Troca a aba da operação aberta (Informações, Dados bancários, Garantias, Envolvidos, Operação, Documentos, Assinaturas, KYC, Crédito, Cessões ou Histórico). Algumas abas só aparecem conforme a operação. Abas: ${tabIds}.`,
  input: z.object({ tab: z.enum(CREDIT_NOTE_TAB_IDS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab }),
});

export const creditNoteDetailScreen = defineScreen({
  id: 'creditNote.detail',
  path: `${CREDIT_NOTE_LIST_PATH}/$creditNoteId`,
  title: msg`Operação`,
  description: msg`Ficha de uma operação de crédito: cabeçalho com status, etapa e resumo dos valores, as abas (informações, dados bancários, garantias, envolvidos, operação, documentos, assinaturas, KYC, crédito, cessões e histórico) e a barra de ações conforme o status.`,
  permission: can('CreditNote', 'Read'),
  place: { icon: 'receipt-text', parent: 'creditNote.list', record: msg`Operação`, tabs: CREDIT_NOTE_TABS },
  actions: [
    creditNoteRefresh,
    creditNoteOpenTab,
    creditNoteSubmitApproval,
    creditNoteApproveCompliance,
    creditNoteRejectCompliance,
    creditNoteApproveCredit,
    creditNoteRejectCredit,
    creditNoteApproveInstrument,
    creditNoteCancel,
    creditNoteDelete,
    creditNoteUnlock,
    creditNoteComment,
    creditNoteAccountOpeningReview,
    creditNoteLinkEmployer,
    creditNoteSetClosureStatus,
    creditNoteRemoveRestrictions,
    creditNoteCloseTransitoryAccount,
    creditNoteSendToWallet,
    ...CREDIT_NOTE_DOCUMENT_ACTIONS,
    // Formulário (docs/execucao/CREDIT-NOTE-FORMULARIO-RELATORIO.md): a tela prende cada uma à operação aberta.
    creditNoteSave,
    creditNoteRecoverSimulation,
    creditNoteClone,
    creditNoteCloneAsNew,
  ],
});

/** O número da operação com os primeiros dígitos ocultos (U46 D2-C): mostra no máximo os 3 finais e sempre oculta ao menos 1. */
export function maskCreditNoteNo(no: number | null | undefined): string {
  if (no === null || no === undefined) return '-';
  const digits = String(no);
  const visible = Math.min(3, digits.length - 1);
  return `#${'•'.repeat(digits.length - visible)}${digits.slice(digits.length - visible)}`;
}

/** O que o agente lê do detalhe (U24, U46 D2-C), no idioma da tela: status, etapa, produto e aba; número mascarado, sem nome nem documento. */
export function creditNoteDetailContext(note: CreditNote | undefined, visibleActions: readonly string[], tab: string, restrictions: readonly { readonly id: string; readonly attributeType: string }[] = []): ScreenContext {
  if (!note) return { summary: t`Operação de crédito carregando.`, fields: {}, sensitivity: 'restricted' };
  const number = maskCreditNoteNo(note.creditNoteNo);
  const status = CREDIT_NOTE_STATUS.label(i18n, note.status, note.statusDisplay ?? note.status) ?? note.status;
  const product = note.productIdDisplay ?? '-';
  const tabTitle = CREDIT_NOTE_TABS.find((item) => item.id === tab);
  const tabLabel = tabTitle ? i18n._(tabTitle.label) : tab;
  const actions = visibleActions.join(', ');
  return {
    summary: [
      t`Operação de crédito ${number}, status ${status}, produto ${product}, aba ${tabLabel}.`,
      visibleActions.length > 0 ? t`Ações disponíveis: ${actions}.` : t`Nenhuma ação disponível.`,
    ].join(' '),
    entity: { type: 'CreditNote', id: note.id },
    fields: {
      creditNoteNo: number,
      status: note.status,
      product: note.productIdDisplay ?? null,
      principalAmountInCents: note.amortization?.principalAmountInCents ?? null,
      startDate: note.amortization?.startDate ?? null,
      // As restrições do tomador aberto: o único alvo aceito por `creditNote.removeRestrictions` (B1).
      restrictions: restrictions.map((item) => ({ id: item.id, attributeType: item.attributeType })),
    },
    sensitivity: 'restricted',
  };
}
